import type { MessageStatus, JarMessage, JarRecord, SessionUser } from '../shared/contracts.js';
import { supabase } from './supabase.js';

const namePattern = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
const MIN_TITLE = 2;
const MAX_TITLE = 80;
const MAX_DESCRIPTION = 200;
const MIN_BODY = 1;
const MAX_BODY = 1500;

type PgError = { code?: string; message?: string };

function mapJarError(err: PgError): Error {
  const code = err.code;
  const msg = err.message ?? '';
  if (code === '23505') {
    // unique_violation — only UNIQUE column on `jars` is `name`.
    return new Error('That jar name is taken. Please pick another.');
  }
  if (code === '23514') {
    if (/jars_name_check/i.test(msg)) {
      return new Error('Jar name must be 3-30 chars using lowercase letters, numbers, or dashes.');
    }
    if (/jars_title_check/i.test(msg)) {
      return new Error(`Title must be ${MIN_TITLE}-${MAX_TITLE} characters.`);
    }
    if (/jars_description_check/i.test(msg)) {
      return new Error(`Description must be ${MAX_DESCRIPTION} characters or fewer.`);
    }
  }
  return new Error(msg || 'Could not create jar.');
}

function requireOk<T>(value: T | null, error: { message?: string } | null): T {
  if (error || !value) throw new Error(error?.message ?? 'Request failed');
  return value;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw new Error('Please log in first');
  return data.user.id;
}

export const api = {
  async signUpWithEmail(email: string, password: string): Promise<{ needsConfirmation: boolean }> {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw new Error(error.message);
    // When "Confirm email" is enabled in Supabase Auth, signUp returns a user
    // but no session — the caller must prompt the user to click the email
    // link before they can sign in. When it's off, both user and session are
    // populated and signup auto-logs in.
    return { needsConfirmation: !data.session };
  },
  async loginWithEmail(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  },
  async loginWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (error) throw new Error(error.message);
  },
  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  },
  async me(): Promise<SessionUser | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw new Error(error.message);
    const user = data.session?.user;
    if (!user) return null;
    return { id: user.id, email: user.email ?? null };
  },
  async listPublicJars(): Promise<JarRecord[]> {
    const { data, error } = await supabase
      .from('jars')
      .select('id, name, title, description, is_public, canvas_x, canvas_y, owner_id')
      .eq('is_public', true)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      title: row.title,
      description: row.description ?? null,
      isPublic: row.is_public,
      canvasX: row.canvas_x ?? 0,
      canvasY: row.canvas_y ?? 0,
      ownerId: row.owner_id,
    }));
  },
  async createJar(payload: { name: string; title: string; description?: string; isPublic: boolean }): Promise<JarRecord> {
    const name = payload.name.toLowerCase().trim();
    if (!namePattern.test(name)) {
      throw new Error('Jar name must be 3-30 chars using lowercase letters, numbers, or dashes.');
    }
    const title = payload.title.trim();
    if (title.length < MIN_TITLE || title.length > MAX_TITLE) {
      throw new Error(`Title must be ${MIN_TITLE}-${MAX_TITLE} characters.`);
    }
    const description = payload.description?.trim() || null;
    if (description && description.length > MAX_DESCRIPTION) {
      throw new Error(`Description must be ${MAX_DESCRIPTION} characters or fewer.`);
    }
    const ownerId = await requireUserId();
    const random = () => Math.round((Math.random() - 0.5) * 2800);
    const { data, error } = await supabase
      .from('jars')
      .insert({
        owner_id: ownerId,
        name,
        title,
        description,
        is_public: payload.isPublic,
        canvas_x: random(),
        canvas_y: random(),
      })
      .select('id, name, title, description, is_public, canvas_x, canvas_y, owner_id')
      .single();
    if (error) throw mapJarError(error as PgError);
    const jar = requireOk(data, null);
    return {
      id: jar.id,
      name: jar.name,
      title: jar.title,
      description: jar.description ?? null,
      isPublic: jar.is_public,
      canvasX: jar.canvas_x ?? 0,
      canvasY: jar.canvas_y ?? 0,
      ownerId: jar.owner_id,
    };
  },
  async getJarByName(name: string): Promise<JarRecord | null> {
    const { data, error } = await supabase
      .from('jars')
      .select('id, name, title, description, is_public, canvas_x, canvas_y, owner_id')
      .eq('name', name.toLowerCase())
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return {
      id: data.id,
      name: data.name,
      title: data.title,
      description: data.description ?? null,
      isPublic: data.is_public,
      canvasX: data.canvas_x ?? 0,
      canvasY: data.canvas_y ?? 0,
      ownerId: data.owner_id,
    };
  },
  async listMyJars(): Promise<JarRecord[]> {
    const ownerId = await requireUserId();
    const { data, error } = await supabase
      .from('jars')
      .select('id, name, title, description, is_public, canvas_x, canvas_y, owner_id')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      title: row.title,
      description: row.description ?? null,
      isPublic: row.is_public,
      canvasX: row.canvas_x ?? 0,
      canvasY: row.canvas_y ?? 0,
      ownerId: row.owner_id,
    }));
  },
  async deleteJar(jarId: string): Promise<void> {
    const ownerId = await requireUserId();
    const { error } = await supabase.from('jars').delete().eq('id', jarId).eq('owner_id', ownerId);
    if (error) throw new Error(error.message);
  },
  async postMessage(
    name: string,
    payload: { body: string; senderName?: string | null },
  ): Promise<{ id: string; userId: string | null; createdAt: string }> {
    const jar = await this.getJarByName(name);
    if (!jar) throw new Error('Jar not found');
    // Anon posts are allowed — don't throw if there's no session.
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id ?? null;
    // Private jars are writable only by their owner. Public jars accept
    // anonymous and authenticated posts.
    if (!jar.isPublic && jar.ownerId !== userId) throw new Error('Jar not found');
    const trimmed = payload.body.trim();
    if (trimmed.length < MIN_BODY) throw new Error('Your note is empty.');
    if (trimmed.length > MAX_BODY) throw new Error(`Your note exceeds the ${MAX_BODY.toLocaleString()} letter cap. Please shorten it.`);
    const { data, error } = await supabase
      .from('messages')
      .insert({
        jar_id: jar.id,
        user_id: userId,
        body: trimmed,
        sender_name: payload.senderName?.trim() || null,
        status: 'visible',
      })
      .select('id, user_id, created_at')
      .single();
    if (error) {
      // 23514 = Postgres check_violation. If the body-length check rejects the
      // insert (e.g. the server cap is tighter than the client's), show a
      // friendly message instead of the raw SQL error text.
      const code = (error as { code?: string }).code;
      if (code === '23514' && /messages_body_check/i.test(error.message)) {
        throw new Error('Your note is too long. Please shorten it.');
      }
      throw new Error(error.message);
    }
    if (!data) throw new Error('Could not save your message.');
    return { id: data.id, userId: data.user_id ?? null, createdAt: data.created_at };
  },
  async listJarMessages(jarId: string): Promise<JarMessage[]> {
    // Public read: visitors and anon users load the pile via the
    // "Public read visible messages on public jars" RLS policy. Oldest-first
    // so hydration piles new messages on top of old, matching post order.
    const { data, error } = await supabase
      .from('messages')
      .select('id, jar_id, user_id, body, sender_name, status, created_at')
      .eq('jar_id', jarId)
      .eq('status', 'visible')
      .order('created_at', { ascending: true })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      id: row.id,
      jarId: row.jar_id,
      userId: row.user_id ?? null,
      body: row.body,
      senderName: row.sender_name,
      status: row.status as MessageStatus,
      createdAt: row.created_at,
    }));
  },
  async listMyMessages(jarId: string): Promise<JarMessage[]> {
    const ownerId = await requireUserId();
    const { data: jarCheck, error: jarError } = await supabase
      .from('jars')
      .select('id')
      .eq('id', jarId)
      .eq('owner_id', ownerId)
      .maybeSingle();
    if (jarError || !jarCheck) throw new Error('Jar not found');

    const { data, error } = await supabase
      .from('messages')
      .select('id, jar_id, user_id, body, sender_name, status, created_at')
      .eq('jar_id', jarId)
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => ({
      id: row.id,
      jarId: row.jar_id,
      userId: row.user_id ?? null,
      body: row.body,
      senderName: row.sender_name,
      status: row.status as MessageStatus,
      createdAt: row.created_at,
    }));
  },
  async updateMessageStatus(messageId: string, status: MessageStatus): Promise<void> {
    const { error } = await supabase.from('messages').update({ status }).eq('id', messageId);
    if (error) throw new Error(error.message);
  },
};
